/**
 * Full-viewport dimmed backdrop behind the inline search bar, appended
 * to document.body (see comment in the block file for why).
 *
 * Also keeps the search bar/backdrop's top offset in sync with the
 * header's *actual* on-screen position, read live via
 * getBoundingClientRect(). This is deliberately not tied to a single
 * CSS custom property like --header-height or --header-group-height:
 * the announcement bar isn't sticky and scrolls away with the page,
 * so the real gap between viewport-top and the header shrinks
 * continuously as you scroll. Measuring the header directly tracks
 * that automatically, whatever header.js is doing internally.
 */
function initSearchBackdrop(dialogId) {
  const dialogComponent = document.getElementById(dialogId);
  if (!dialogComponent || dialogComponent.dataset.hasBackdrop) return;
  dialogComponent.dataset.hasBackdrop = 'true';

  const headerComponent = document.getElementById('header-component');

  let overlay = document.querySelector('.search-backdrop');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.className = 'search-backdrop';
    overlay.setAttribute('aria-hidden', 'true');
    document.body.appendChild(overlay);
  }

  function updateOffset() {
    if (!headerComponent) return;
    const bottom = Math.round(headerComponent.getBoundingClientRect().bottom);
    document.documentElement.style.setProperty('--search-bar-offset', `${Math.max(bottom, 0)}px`);
  }

  let ticking = false;
  function onScrollOrResize() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      updateOffset();
      ticking = false;
    });
  }

  function hideEverything() {
    overlay.classList.remove('search-backdrop--visible');
    window.removeEventListener('scroll', onScrollOrResize);
    window.removeEventListener('resize', onScrollOrResize);

    // Safety net for header-menu.liquid's own mega-menu backdrop, a
    // separate element shown/hidden purely via hover/focus events on
    // <header-menu>. Touch devices can synthesize a hover-start from a
    // tap without a matching hover-end, leaving it stuck visible.
    document.querySelectorAll('.mega-menu-backdrop').forEach((backdrop) => {
      backdrop.classList.remove('mega-menu-backdrop--visible');
    });
  }

  dialogComponent.addEventListener('dialog:open', () => {
    updateOffset();
    overlay.classList.add('search-backdrop--visible');
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
  });

  dialogComponent.addEventListener('dialog:close', hideEverything);

  // XCLOUD INTEROP: xcloud's mobile Cancel button
  // (.cloud-search-mobile__cancel) belongs to its own results takeover
  // UI. Confirmed via testing that by the time this button is tapped,
  // our native dialog already reports open equal to false, meaning
  // xcloud (or the browser) has closed it independently, most likely
  // as a defensive measure to avoid two overlays stacking. That means
  // our dialog's own closeDialog method and dialog:close event never
  // run at this point, so nothing was ever telling our overlay to
  // hide. Calling hideEverything directly here, rather than going
  // through closeDialog, works regardless of the dialog's actual
  // state. Event delegation on document, since xcloud's markup is
  // injected dynamically and a direct listener would be attached too
  // early or lost on re-render.
  document.addEventListener('click', (event) => {
    if (!event.target.closest('.cloud-search-mobile__cancel')) return;
    hideEverything();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initSearchBackdrop('search-modal'));
} else {
  initSearchBackdrop('search-modal');
}